import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, HelpCircle, MessageSquare, ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";

const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none";

const Recovery = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("start"); // start | questions | sms-code | reset | done | sent
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState("");

  useEffect(() => {
    if (params.get("sms") === "pagato") setPhase("sms-code");
  }, [params]);

  const run = async (fn) => {
    setBusy(true); setError(""); setNotice("");
    try { await fn(); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  const byEmail = () => run(async () => {
    await api.post("/auth/forgot-password", { email, website: hp });
    setPhase("sent");
  });

  const loadQuestions = () => run(async () => {
    const { data } = await api.get(`/auth/recovery/questions?email=${encodeURIComponent(email)}`);
    setQuestions(data.questions);
    setAnswers({});
    setPhase("questions");
  });

  const submitAnswers = () => run(async () => {
    const { data } = await api.post("/auth/recovery/answer", { email, answers, website: hp });
    setToken(data.recovery_token);
    setPhase("reset");
  });

  const paySms = () => run(async () => {
    const { data } = await api.post("/auth/recovery/sms", { email, origin_url: window.location.origin, website: hp });
    window.location.href = data.checkout_url;
  });

  const verifySms = () => run(async () => {
    const { data } = await api.post("/auth/recovery/sms/verify", { email, code: smsCode });
    setToken(data.recovery_token);
    setPhase("reset");
  });

  const resetPw = () => run(async () => {
    await api.post("/auth/recovery/reset", { token, password });
    setPhase("done");
  });

  const methods = [
    { id: "email", icon: Mail, title: t("rec_by_email"), sub: t("rec_by_email_sub"), fn: byEmail, testid: "recovery-method-email" },
    { id: "questions", icon: HelpCircle, title: t("rec_by_questions"), sub: t("rec_by_questions_sub"), fn: loadQuestions, testid: "recovery-method-questions" },
    { id: "sms", icon: MessageSquare, title: t("rec_by_sms"), sub: t("rec_by_sms_sub"), fn: paySms, testid: "recovery-method-sms" },
  ];

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-5 py-16" data-testid="recovery-page">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-card p-8 sm:p-10">
        <h1 className="font-display font-bold text-2xl mb-2">{t("rec_title")}</h1>
        <p className="text-mute text-sm mb-8">{t("rec_sub")}</p>

        {phase === "start" && (
          <div className="space-y-5">
            <input data-testid="recovery-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder={t("rec_email_label")} className={field} />
            <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
            <div className="space-y-3">
              {methods.map((m) => (
                <button key={m.id} data-testid={m.testid} disabled={busy || !email.includes("@")} onClick={m.fn}
                  className="w-full flex items-center gap-4 rounded-xl border border-white/10 bg-black/30 px-5 py-4 text-left hover:border-ember/60 transition-colors disabled:opacity-40">
                  <m.icon className="w-5 h-5 text-ember shrink-0" />
                  <span>
                    <span className="block text-sm font-semibold">{m.title}</span>
                    <span className="block text-xs text-mute mt-0.5">{m.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "sent" && (
          <div data-testid="recovery-email-sent">
            <p className="text-gold text-sm mb-6">{t("auth_forgot_sent")}</p>
            <Link to="/accedi" data-testid="recovery-back-login" className="inline-flex items-center gap-2 text-sm text-ember hover:text-gold transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t("rec_back")}
            </Link>
          </div>
        )}

        {phase === "questions" && (
          <div className="space-y-4" data-testid="recovery-questions-form">
            {questions.map((q) => (
              <div key={q}>
                <label className="block text-xs text-mute mb-1.5">{t(q)}</label>
                <input data-testid={`recovery-answer-${q}`} className={field} placeholder={t("rec_answer_ph")}
                  value={answers[q] || ""} onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} />
              </div>
            ))}
            <button data-testid="recovery-verify-answers" disabled={busy} onClick={submitAnswers}
              className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
              {t("rec_verify")}
            </button>
          </div>
        )}

        {phase === "sms-code" && (
          <div className="space-y-4" data-testid="recovery-sms-form">
            <p className="text-sm text-mist">{t("rec_sms_paid_hint")}</p>
            {!email && (
              <input data-testid="recovery-sms-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder={t("rec_email_label")} className={field} />
            )}
            <input data-testid="recovery-sms-code" value={smsCode} onChange={(e) => setSmsCode(e.target.value)}
              placeholder={t("rec_sms_code")} maxLength={6} inputMode="numeric" className={`${field} text-center tracking-[0.5em] font-mono2`} />
            <button data-testid="recovery-sms-verify" disabled={busy || smsCode.length !== 6 || !email.includes("@")} onClick={verifySms}
              className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
              {t("rec_sms_verify")}
            </button>
          </div>
        )}

        {phase === "reset" && (
          <div className="space-y-4" data-testid="recovery-reset-form">
            <input data-testid="recovery-new-password" type="password" minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder={t("rec_new_pw")} className={field} />
            <button data-testid="recovery-reset-submit" disabled={busy || password.length < 6} onClick={resetPw}
              className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
              {t("rec_reset_btn")}
            </button>
          </div>
        )}

        {phase === "done" && (
          <div data-testid="recovery-done">
            <p className="text-gold text-sm mb-6">{t("rec_done")}</p>
            <Link to="/accedi" data-testid="recovery-done-login"
              className="inline-flex rounded-full bg-ember px-6 py-3 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
              {t("nav_login")}
            </Link>
          </div>
        )}

        {error && <p data-testid="recovery-error" className="text-sm text-red-400 mt-4">{error}</p>}
        {notice && <p className="text-sm text-gold mt-4">{notice}</p>}
      </div>
    </main>
  );
};

export default Recovery;
